import { create } from 'zustand';
import {
  getRoutinesToPush,
  getRoutineTombstones,
  countUnsyncedSessions,
  getAllPendingUploads,
  countDirtyLibrary
} from '../db/operations.js';
import { waitForGuestClaim } from '../db/claim.js';
import { syncPendingSessions } from '../sync/sessionSync.js';
import { useAuthStore } from './authStore.js';
import { syncRoutines, getCurrentUserId, ROUTINES_CHANGED_EVENT } from '../sync/routineSync.js';
import { syncTabUploads, resetNetworkBackoff } from '../sync/tabUploadSync.js';
import { syncFavorites } from '../sync/favoriteSync.js';
import { syncPlaylists } from '../sync/playlistSync.js';
import { syncLyricSheets } from '../sync/lyricSync.js';
import { refreshOwnTabStatuses } from '../sync/tabStatusSync.js';
import { syncTabEdits, refreshTabExtras } from '../sync/tabEditSync.js';
import { getQueuedEdits } from '../db/tabEdits.js';
import { getPendingDifficultyRows } from '../db/difficulty.js';
import { LIBRARY_CHANGED_EVENT } from '../sync/events.js';

export type SyncStatusState = 'synced' | 'syncing' | 'offline' | 'error';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface SyncStoreState {
  status: SyncStatusState;
  queuedSessionsCount: number;
  dirtyRoutinesCount: number;
  /** FRE-142/143: pending tab uploads + dirty playlists and favorites of the current user (store field only, no UI yet). */
  pendingLibraryCount: number;
  lastSyncedAt: Date | null;
  deferredPrompt: BeforeInstallPromptEvent | null;
  errorMessage: string | null;

  // Actions
  refreshCounts: () => Promise<void>;
  triggerSync: () => Promise<{ success: boolean; message?: string }>;
  setDeferredPrompt: (prompt: BeforeInstallPromptEvent | null) => void;
  setStatus: (status: SyncStatusState) => void;
  initSyncStore: () => () => void;
}

type SyncResult = { success: boolean; message?: string };

let syncInFlight: Promise<SyncResult> | null = null;
let syncRerunRequested = false;

export const useSyncStore = create<SyncStoreState>((set, get) => {
  const runSync = async (): Promise<SyncResult> => {
    const { isAuthenticated, isGuest } = useAuthStore.getState();

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      set({ status: 'offline' });
      await get().refreshCounts();
      return { success: false, message: 'دستگاه در حالت آفلاین است.' };
    }

    if (!isAuthenticated || isGuest) {
      await get().refreshCounts();
      set({ status: 'synced' });
      return { success: true, message: 'تغییرات با موفقیت همگام شد.' };
    }

    set({ status: 'syncing', errorMessage: null });

    // FRE-142 D11: every step is isolated; the first error is reported after all steps ran.
    let firstError: unknown = null;
    const step = async (fn: () => Promise<unknown>) => {
      try {
        await fn();
      } catch (err) {
        console.warn('Sync step failed:', err);
        if (firstError === null) firstError = err;
      }
    };

    await step(() => waitForGuestClaim());
    await step(() => syncTabUploads());
    await step(() => syncTabEdits());
    // Sessions keep waiting for routines (block ids)
    await step(async () => {
      await syncRoutines();
      await syncPendingSessions();
    });
    await step(() => syncFavorites());
    await step(() => syncPlaylists());
    await step(() => syncLyricSheets());
    await step(() => refreshOwnTabStatuses());
    await step(() => refreshTabExtras());

    await get().refreshCounts();
    if (firstError !== null) {
      const isCurrentlyOffline = typeof navigator !== 'undefined' && !navigator.onLine;
      const msg = (firstError as { message?: string })?.message || 'خطا در برقراری ارتباط با سرور';
      set({ status: isCurrentlyOffline ? 'offline' : 'error', errorMessage: msg });
      return { success: false, message: msg };
    }
    set({ status: 'synced', lastSyncedAt: new Date(), errorMessage: null });
    return { success: true, message: 'تغییرات با موفقیت همگام شد.' };
  };

  return {
    status: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'synced',
    queuedSessionsCount: 0,
    dirtyRoutinesCount: 0,
    pendingLibraryCount: 0,
    lastSyncedAt: null,
    deferredPrompt: null,
    errorMessage: null,

    setStatus: (status) => set({ status }),

    setDeferredPrompt: (deferredPrompt) => set({ deferredPrompt }),

    refreshCounts: async () => {
      try {
        const userId = getCurrentUserId();
        const unsyncedSessions = await countUnsyncedSessions(userId);
        let dirtyRoutines = 0;
        if (userId) {
          const toPush = await getRoutinesToPush(userId);
          const tombstones = await getRoutineTombstones(userId);
          dirtyRoutines = toPush.length + tombstones.length;
        }
        const pendingLibrary = userId
          ? (await getAllPendingUploads(userId)).length +
            (await countDirtyLibrary(userId)) +
            (await getPendingDifficultyRows(userId)).length +
            (await getQueuedEdits(userId)).length
          : 0;
        set({
          queuedSessionsCount: unsyncedSessions,
          dirtyRoutinesCount: dirtyRoutines,
          pendingLibraryCount: pendingLibrary
        });
      } catch {
        // In case Dexie is not ready yet
      }
    },

    triggerSync: () => {
      // Single in-flight run; a call during a run schedules exactly one more run afterwards.
      if (syncInFlight) {
        syncRerunRequested = true;
        return syncInFlight;
      }
      syncInFlight = (async () => {
        try {
          let result: SyncResult;
          do {
            syncRerunRequested = false;
            result = await runSync();
          } while (syncRerunRequested);
          return result;
        } finally {
          syncInFlight = null;
        }
      })();
      return syncInFlight;
    },

    initSyncStore: () => {
      // Initial calculation
      get().refreshCounts();

      const handleOnline = () => {
        set({ status: 'syncing' });
        void resetNetworkBackoff().finally(() => get().triggerSync());
      };

      const handleOffline = () => {
        set({ status: 'offline' });
        get().refreshCounts();
      };

      const handleBeforeInstall = (e: Event) => {
        e.preventDefault();
        set({ deferredPrompt: e as BeforeInstallPromptEvent });
      };

      const handleCountsChanged = () => {
        get().refreshCounts();
      };

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
      window.addEventListener(ROUTINES_CHANGED_EVENT, handleCountsChanged);
      window.addEventListener(LIBRARY_CHANGED_EVENT, handleCountsChanged);

      // FRE-142: sync right after every account sign-in (also account switches)
      const accountOf = (st: ReturnType<typeof useAuthStore.getState>) =>
        st.token && !st.isGuest ? st.user?.id : undefined;
      const unsubscribeAuth = useAuthStore.subscribe((state, prev) => {
        const uid = accountOf(state);
        if (uid && uid !== accountOf(prev)) void get().triggerSync();
      });

      // Initial background sync check if online
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        get().triggerSync();
      }

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        window.removeEventListener(ROUTINES_CHANGED_EVENT, handleCountsChanged);
        window.removeEventListener(LIBRARY_CHANGED_EVENT, handleCountsChanged);
        unsubscribeAuth();
      };
    }
  };
});
